import { createFileRoute } from "@tanstack/react-router";
import {
	Banknote,
	Check,
	Compass,
	Download,
	Heart,
	Megaphone,
	Scale,
	Sparkles,
	Star,
	Target,
	Users,
	UserX,
	Wallet,
} from "lucide-react";
import { BookingFab } from "#/components/booking-fab";
import { LeadForm } from "#/components/lead-form";
import { LogoMark } from "#/components/logo";
import { Accordion, ForwardArrow } from "#/components/ui";
import { VariantChrome } from "#/components/variant-chrome";
import { useI18n } from "#/lib/i18n";
import { getDefaultTraining } from "#/lib/training";

export const Route = createFileRoute("/hero-c")({
	head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
	loader: () => getDefaultTraining(),
	component: HeroC,
});

const PILLAR_ICONS = [Banknote, Scale, Megaphone];
const PAIN_ICONS = [Compass, Wallet, Scale, UserX];

/** Warm, friendly eyebrow: a short gold rule + soft-tracked teal label. */
function Eyebrow({ children }: { children: React.ReactNode }) {
	return (
		<span className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.16em] text-teal-600">
			<span aria-hidden className="h-px w-7 bg-gold" />
			{children}
		</span>
	);
}

function HeroC() {
	const { t } = useI18n();
	const training = Route.useLoaderData();

	return (
		<div className="overflow-x-clip bg-sand">
			<VariantChrome active="c" />

			{/* ---------- hero ---------- */}
			<section className="grain relative">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_55%_at_85%_0%,rgb(23_166_177/0.10),transparent_60%),radial-gradient(45%_45%_at_0%_20%,rgb(224_166_60/0.12),transparent_60%)]"
				/>
				<div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-16 pt-28 sm:px-8 lg:grid-cols-2 lg:gap-12 lg:pt-32">
					{/* copy */}
					<div className="animate-fade-up">
						{/* social proof leads instead of a format chip */}
						<div className="inline-flex items-center gap-3 rounded-full bg-white/70 px-4 py-2 ring-1 ring-black/5">
							<div className="flex text-gold">
								{[0, 1, 2, 3, 4].map((i) => (
									<Star key={i} className="h-4 w-4 fill-current" />
								))}
							</div>
							<span className="text-xs font-semibold text-ink/70">
								{t.hero.trust}
							</span>
						</div>

						<h1 className="mt-6 font-display font-bold leading-[1.05] text-navy text-[clamp(2.5rem,7vw,4.6rem)]">
							{t.hero.titleLead}{" "}
							<span className="text-teal">{t.hero.titleHighlight}</span>
						</h1>

						<p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/70">
							{t.hero.subtitle}
						</p>

						<ul className="mt-7 flex flex-col gap-2.5">
							{t.hero.bullets.map((b) => (
								<li key={b} className="flex items-center gap-3">
									<span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-teal text-white">
										<Check className="h-3.5 w-3.5" strokeWidth={3} />
									</span>
									<span className="text-[0.95rem] font-medium text-ink">
										{b}
									</span>
								</li>
							))}
						</ul>

						<div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
							<a
								href="#register"
								className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-teal px-8 py-4 text-base font-bold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:bg-teal-600 sm:w-auto"
							>
								{t.hero.ctaPrimary}
								<ForwardArrow className="h-4 w-4 transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
							</a>
							<a
								href="#course"
								className="inline-flex w-full items-center justify-center rounded-full bg-white px-7 py-4 text-base font-bold text-navy ring-1 ring-black/5 transition-colors hover:bg-white/60 sm:w-auto"
							>
								{t.hero.ctaSecondary}
							</a>
						</div>
					</div>

					{/* portrait — soft tilted card + overlapping credential card */}
					<div className="relative mx-auto w-full max-w-sm animate-fade-up [animation-delay:120ms] lg:max-w-none">
						<div className="relative rotate-2 overflow-hidden rounded-[2.5rem] bg-white p-3 shadow-lift transition-transform duration-500 hover:rotate-0">
							<img
								src="/team/ayoub-portrait.webp"
								alt={t.hero.portraitAlt}
								width={700}
								height={840}
								className="aspect-5/6 w-full rounded-4xl object-cover object-top"
							/>
							<div className="px-3 py-4">
								<div className="font-display text-lg font-bold text-navy">
									{t.trainer.name}
								</div>
								<div className="text-sm text-teal-600">{t.trainer.role}</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- stats band ---------- */}
			<section className="px-5 sm:px-8">
				<div className="mx-auto max-w-6xl">
					<div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
						{t.stats.map((stat, i) => (
							<div
								key={stat.label}
								className={`rounded-[1.75rem] bg-white p-6 text-center shadow-card max-lg:last:col-span-2 ${
									i % 2 === 0 ? "sm:rotate-1" : "sm:-rotate-1"
								}`}
							>
								<div className="index-num font-display text-4xl font-bold text-navy lg:text-5xl">
									{stat.value}
								</div>
								<div className="mt-1.5 text-sm font-medium text-ink/60">
									{stat.label}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ---------- pains ---------- */}
			<section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20 lg:py-24">
				<div className="mx-auto max-w-2xl text-center">
					<div className="flex justify-center">
						<Eyebrow>{t.pains.eyebrow}</Eyebrow>
					</div>
					<h2 className="mt-5 font-display font-bold leading-tight text-navy text-[clamp(1.9rem,4.5vw,2.75rem)]">
						{t.pains.heading}
					</h2>
					<p className="mt-4 text-base leading-relaxed text-ink/60">
						{t.pains.sub}
					</p>
				</div>

				<div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{t.pains.items.map((pain, i) => {
						const Icon = PAIN_ICONS[i];
						return (
							<article
								key={pain.title}
								className={`group h-full rounded-4xl bg-white p-6 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift ${
									i % 2 === 1 ? "sm:mt-6" : ""
								}`}
							>
								<span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold-50 text-gold-600 transition-transform group-hover:-rotate-6">
									<Icon className="h-5 w-5" />
								</span>
								<h3 className="mt-5 font-display text-lg font-bold text-navy">
									{pain.title}
								</h3>
								<p className="mt-1.5 text-sm leading-relaxed text-ink/60">
									{pain.text}
								</p>
							</article>
						);
					})}
				</div>

				<div className="mt-10 flex flex-col items-center justify-center gap-3 rounded-4xl bg-teal px-6 py-6 text-center shadow-glow sm:flex-row sm:gap-4">
					<span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-white/15 text-white">
						<Sparkles className="h-5 w-5" />
					</span>
					<p className="font-display text-base font-bold text-white sm:text-lg">
						{t.pains.solution}
					</p>
				</div>
			</section>

			{/* ---------- pillars (solution) ---------- */}
			<section
				id="course"
				className="scroll-mt-24 bg-canvas py-16 sm:py-20 lg:py-24"
			>
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
						<div className="flex flex-col items-start gap-4">
							<Eyebrow>{t.pillars.eyebrow}</Eyebrow>
							<h2 className="max-w-lg font-display font-bold leading-tight text-navy text-[clamp(1.9rem,4.5vw,2.75rem)]">
								{t.pillars.heading}
							</h2>
						</div>
						<p className="max-w-xs text-sm leading-relaxed text-ink/60">
							{t.pillars.sub}
						</p>
					</div>

					<div className="mt-12 grid gap-5 md:grid-cols-3">
						{t.pillars.items.map((pillar, i) => {
							const Icon = PILLAR_ICONS[i];
							return (
								<article
									key={pillar.title}
									className="group relative h-full overflow-hidden rounded-4xl bg-white p-7 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
								>
									<div className="flex items-center justify-between">
										<span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal transition-transform group-hover:-rotate-6 group-hover:bg-teal group-hover:text-white">
											<Icon className="h-6 w-6" />
										</span>
										<span className="index-num font-display text-4xl font-bold text-gold-100 transition-colors group-hover:text-gold">
											{`0${i + 1}`}
										</span>
									</div>
									<h3 className="mt-8 font-display text-xl font-bold text-navy">
										{pillar.title}
									</h3>
									<p className="mt-2 text-sm leading-relaxed text-ink/60">
										{pillar.text}
									</p>
								</article>
							);
						})}
					</div>

					{/* technical-card downloads */}
					<div className="mt-10 flex flex-col items-center justify-center gap-4 rounded-4xl bg-white p-7 shadow-card sm:flex-row">
						<span className="flex items-center gap-2.5 text-sm font-bold text-navy">
							<span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold-50 text-gold-600">
								<Download className="h-4 w-4" />
							</span>
							{t.pillars.downloadLabel}
						</span>
						<div className="flex flex-wrap items-center justify-center gap-3">
							<a
								href="/downloads/fiche-technique-ar.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-bold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:bg-teal-600"
							>
								<Download className="h-4 w-4" />
								عربية (AR)
							</a>
							<a
								href="/downloads/fiche-technique-fr.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full bg-sand px-5 py-2.5 text-sm font-bold text-navy ring-1 ring-black/5 transition-colors hover:bg-teal-50"
							>
								<Download className="h-4 w-4" />
								Français (FR)
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- trainer ---------- */}
			<section id="trainer" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
					<div className="relative order-2 lg:order-1">
						<div className="relative -rotate-2 overflow-hidden rounded-[2.5rem] bg-white p-3 shadow-lift transition-transform duration-500 hover:rotate-0">
							<img
								src="/team/ayoub-avatar.webp"
								alt={t.trainer.avatarAlt}
								width={460}
								height={460}
								className="aspect-square w-full rounded-4xl object-cover"
							/>
							<div className="px-3 py-4">
								<div className="font-display text-lg font-bold text-navy">
									{t.trainer.name}
								</div>
								<div className="text-sm text-teal-600">{t.trainer.role}</div>
							</div>
						</div>

						<div className="absolute top-6 flex rotate-2 animate-float items-center gap-2.5 rounded-2xl bg-navy px-4 py-3 shadow-lift ltr:-right-3 rtl:-left-3 sm:ltr:-right-7 sm:rtl:-left-7">
							<span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-navy">
								<Users className="h-5 w-5" />
							</span>
							<div>
								<div className="index-num text-sm font-bold text-white">
									+10,000
								</div>
								<div className="text-[0.7rem] text-white/60">
									{t.trainer.statLabel}
								</div>
							</div>
						</div>
					</div>

					<div className="order-1 lg:order-2">
						<div className="flex flex-col items-start gap-4">
							<Eyebrow>{t.trainer.eyebrow}</Eyebrow>
							<h2 className="font-display font-bold leading-tight text-navy text-[clamp(1.9rem,4.5vw,2.75rem)]">
								{t.trainer.heading}
							</h2>
							<p className="text-base leading-relaxed text-ink/60">
								{t.trainer.sub}
							</p>
							<p className="text-sm leading-relaxed text-ink/60">
								{t.trainer.bio}
							</p>
						</div>

						<div className="mt-8">
							<Accordion items={t.trainer.why} />
						</div>
					</div>
				</div>
			</section>

			{/* ---------- outcomes ---------- */}
			<section
				id="learn"
				className="scroll-mt-24 bg-canvas py-16 sm:py-20 lg:py-24"
			>
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="mb-6 rounded-[2.5rem] bg-teal-50 p-8 shadow-card sm:p-10">
						<div className="mb-5 flex items-center gap-3">
							<span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-teal text-white">
								<Compass className="h-5 w-5" />
							</span>
							<h2 className="font-display text-2xl font-bold leading-tight text-navy sm:text-3xl">
								{t.outcomes.audienceHeading}
							</h2>
						</div>
						<div className="flex flex-wrap gap-2.5">
							{t.outcomes.audience.map((item) => (
								<span
									key={item}
									className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy shadow-xs ring-1 ring-teal/10"
								>
									{item}
								</span>
							))}
						</div>
					</div>

					<div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
						<div className="rounded-[2.5rem] bg-navy p-8 shadow-lift sm:p-10">
							<div className="mb-6 flex items-center gap-3">
								<span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-white/10 text-gold">
									<Target className="h-5 w-5" />
								</span>
								<h2 className="font-display text-xl font-bold text-white sm:text-2xl">
									{t.outcomes.objectivesHeading}
								</h2>
							</div>
							<ul className="flex flex-col gap-4">
								{t.outcomes.objectives.map((item, i) => (
									<li key={item} className="flex items-start gap-4">
										<span className="index-num mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold/15 font-display text-xs font-bold text-gold">
											{i + 1}
										</span>
										<span className="text-sm leading-relaxed text-white/80">
											{item}
										</span>
									</li>
								))}
							</ul>
						</div>

						<div className="flex flex-col justify-center rounded-[2.5rem] bg-gold-50 p-8 shadow-card ring-1 ring-gold/20 sm:p-10">
							<span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold text-navy">
								<Heart className="h-5 w-5" />
							</span>
							<div className="index-num mt-5 font-display text-6xl font-bold text-gold-600">
								{t.outcomes.practiceValue}
							</div>
							<p className="mt-3 text-sm leading-relaxed text-ink/70">
								{t.outcomes.practiceNote}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- faq ---------- */}
			<section id="faq" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
					<div className="flex flex-col items-start gap-4">
						<Eyebrow>{t.faq.eyebrow}</Eyebrow>
						<h2 className="font-display font-bold leading-tight text-navy text-[clamp(1.9rem,4.5vw,2.75rem)]">
							{t.faq.heading}
						</h2>
						<p className="text-sm leading-relaxed text-ink/60">{t.faq.sub}</p>
					</div>
					<div>
						<Accordion items={t.faq.items} />
					</div>
				</div>
			</section>

			{/* ---------- register ---------- */}
			<section id="register" className="scroll-mt-24 px-5 pb-20 sm:px-8">
				<div className="mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] bg-white shadow-lift">
					<div className="grid lg:grid-cols-[1fr_0.85fr]">
						<div className="p-8 sm:p-12 lg:p-14">
							<Eyebrow>{t.register.eyebrow}</Eyebrow>
							<h2 className="mt-5 max-w-md font-display font-bold leading-tight text-navy text-[clamp(1.9rem,4.5vw,2.6rem)]">
								{t.register.heading}
							</h2>
							<p className="mt-4 max-w-sm text-sm leading-relaxed text-ink/60">
								{t.register.sub}
							</p>

							<div className="mt-8 rounded-4xl bg-sand p-6 shadow-inner sm:p-7">
								<LeadForm source="preview-c" trainingId={training.id} />
							</div>
						</div>

						<div className="relative hidden lg:block">
							<img
								src="/team/ayoub-training.webp"
								alt={t.register.imageAlt}
								width={560}
								height={686}
								className="h-full w-full object-cover"
							/>
							<div
								aria-hidden
								className="absolute inset-0 bg-linear-to-l from-transparent to-white/70 rtl:bg-linear-to-r"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- footer ---------- */}
			<footer className="bg-navy-900 py-12">
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col items-center gap-6 border-b border-white/10 pb-8 text-center sm:flex-row sm:justify-between sm:text-start">
						<LogoMark className="h-9 w-auto" />
						<p className="max-w-xs text-sm text-white/50">{t.footer.tagline}</p>
						<a
							href="#register"
							className="rounded-full bg-teal px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-teal-600"
						>
							{t.footer.book}
						</a>
					</div>
					<div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/40 sm:flex-row">
						<p>{t.footer.rights}</p>
						<p dir="ltr">moustachiracademy@moustachir.dz · www.moustachir.dz</p>
					</div>
				</div>
			</footer>

			<BookingFab href="#register" />
		</div>
	);
}
